import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, StateToken } from '@ngxs/store';
import { insertItem, patch, removeItem, updateItem } from '@ngxs/store/operators';
import { tap } from 'rxjs';
import { AdminNonConformanceRoutes } from 'src/app/core/enums/routes.enum';
import { ApiResponse } from 'src/app/core/models/api-response';
import { OnSuccess } from 'src/app/shared/decorators/on-success.decorator';
import { IdName } from 'src/app/shared/models/id-name';
import { ResetCurrentFilter } from 'src/app/core/state/main-table-filter.actions';
import {
  CreateCause,
  DeleteCause,
  LoadAllCauses,
  LoadCauseById,
  LoadCauses,
  UpdateCause,
  ClearCurrentCause,
} from './non-conformance-cause.actions';
import { CauseApiService } from '../../services/cause/cause-api.service';

export interface CauseStateModel {
  causes: IdName[];
  allCauses: IdName[];
  causeTotalPages: number;
  currentCause: IdName | undefined;
}

const NC_CAUSE_STATE_TOKEN = new StateToken<CauseStateModel>('ncCauses');

@State<CauseStateModel>({
  name: NC_CAUSE_STATE_TOKEN,
  defaults: {
    causes: [],
    allCauses: [],
    causeTotalPages: 0,
    currentCause: undefined,
  },
})
@Injectable()
export class NonConformanceCauseState {
  constructor(private service: CauseApiService) {}

  @Action(LoadCauses)
  loadCauses({ getState, setState }: StateContext<CauseStateModel>, { filter }: LoadCauses) {
    return this.service.loadCauses(filter).pipe(
      tap((response: ApiResponse<IdName[]>) => {
        const state = getState();
        setState({
          ...state,
          causes: response.data,
          causeTotalPages: response.totalPages,
        });
      })
    );
  }

  @Action(LoadAllCauses, { cancelUncompleted: true })
  loadAllCauses(ctx: StateContext<CauseStateModel>) {
    return this.service.loadAllCauses().pipe(
      tap((allCauses: IdName[]) => {
        ctx.setState(patch({ allCauses }));
      })
    );
  }

  @Action(LoadCauseById, { cancelUncompleted: true })
  loadCauseById({ getState, setState }: StateContext<CauseStateModel>, { id }: LoadCauseById) {
    const { causes } = getState();
    const exist = causes.some((i) => i.id === id);
    return this.service.loadCauseById(id).pipe(
      tap((cause: IdName) => {
        setState(
          patch({
            causes: exist
              ? updateItem<IdName>((toUpdate) => toUpdate?.id === id, cause)
              : insertItem<IdName>(cause),
            currentCause: cause,
          })
        );
      })
    );
  }

  @Action(CreateCause)
  @OnSuccess({ url: AdminNonConformanceRoutes.Cause, message: 'causeAdded' })
  createCause({ setState }: StateContext<CauseStateModel>, { cause }: CreateCause) {
    return this.service.createCause(cause).pipe(
      tap((response: IdName) => {
        setState(
          patch({
            causes: insertItem<IdName>(response),
          })
        );
      })
    );
  }

  @Action(UpdateCause)
  @OnSuccess({ url: AdminNonConformanceRoutes.Cause, message: 'causeUpdated' })
  updateCause({ setState }: StateContext<CauseStateModel>, { cause }: UpdateCause) {
    return this.service.updateCause(cause).pipe(
      tap(() => {
        setState(
          patch({
            causes: updateItem<IdName>((toUpdate) => toUpdate?.id === cause.id, cause),
          })
        );
      })
    );
  }

  @Action(DeleteCause, { cancelUncompleted: true })
  @OnSuccess({
    url: AdminNonConformanceRoutes.Cause,
    message: 'causeDeleted',
    dispatch: ResetCurrentFilter,
  })
  deleteCause(ctx: StateContext<CauseStateModel>, { id }: DeleteCause) {
    return this.service.deleteCause(id).pipe(
      tap(() => {
        ctx.setState(
          patch({
            causes: removeItem<IdName>(
              (causeToDelete: IdName | undefined) => causeToDelete?.id === id
            ),
          })
        );
      })
    );
  }

  @Action(ClearCurrentCause)
  clearCurrentCause(ctx: StateContext<CauseStateModel>) {
    ctx.setState(patch({ currentCause: undefined }));
  }

  @Selector()
  static getCausesTotalPages(state: CauseStateModel) {
    return state.causeTotalPages;
  }

  @Selector()
  static causes(state: CauseStateModel) {
    return state.causes;
  }

  @Selector()
  static allCauses(state: CauseStateModel): IdName[] {
    return state.allCauses;
  }

  @Selector()
  static getCauseById(state: CauseStateModel) {
    return (id: string) => {
      return state.causes.find((cause: IdName) => cause.id === id);
    };
  }

  @Selector()
  static getCurrentCause(state: CauseStateModel) {
    return state.currentCause;
  }
}
